"use client";

import Link from "next/link";
import Icon from "@/components/ui/AppIcon";
import { motion } from "framer-motion";

const plans = [
  {
    name: "Starter",
    tagline: "For brands launching or testing",
    price: "Custom",
    priceNote: "Based on volume",
    featured: false,
    features: [
      { text: "Up to 500 orders/month", included: true },
      { text: "Warehousing up to 50 SKUs", included: true },
      { text: "Standard shipping (5–8 days)", included: true },
      { text: "Shopify & WooCommerce integration", included: true },
      { text: "Email support (24hr response)", included: true },
      { text: "Custom packaging", included: false },
      { text: "Dedicated account manager", included: false },
      { text: "Priority shipping options", included: false },
    ],
    cta: "Get a Quote",
  },
  {
    name: "Growth",
    tagline: "For scaling brands doing volume",
    price: "Custom",
    priceNote: "Volume-based pricing",
    featured: true,
    badge: "Most Popular",
    features: [
      { text: "500–5,000 orders/month", included: true },
      { text: "Unlimited SKU management", included: true },
      { text: "Standard + express shipping options", included: true },
      { text: "All platform integrations + API", included: true },
      { text: "Dedicated account manager", included: true },
      { text: "Custom branded packaging", included: true },
      { text: "Priority support (< 2hr response)", included: true },
      { text: "Monthly performance reporting", included: false },
    ],
    cta: "Book a Call",
  },
  {
    name: "Enterprise",
    tagline: "For high-volume brands & agencies",
    price: "Custom",
    priceNote: "Fully negotiated",
    featured: false,
    features: [
      { text: "5,000+ orders/month", included: true },
      { text: "Multi-brand & multi-store support", included: true },
      { text: "All shipping tiers + express", included: true },
      { text: "Full API & custom integrations", included: true },
      { text: "Senior dedicated account manager", included: true },
      { text: "Custom packaging + private label", included: true },
      { text: "SLA-backed support", included: true },
      { text: "Quarterly business reviews", included: true },
    ],
    cta: "Talk to Sales",
  },
];

export default function PricingCards() {
  const staggerContainer = {
    initial: {},
    whileInView: {
      transition: {
        staggerChildren: 0.18
      }
    },
    viewport: { once: true, margin: "-60px" }
  };

  const cardVariant = {
    initial: { opacity: 0, y: 50, scale: 0.92 },
    whileInView: { opacity: 1, y: 0, scale: 1 },
    transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] }
  };

  return (
    <section className="py-16 sm:py-20 lg:py-24 bg-secondary-dark">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8"
          variants={staggerContainer}
          initial="initial"
          whileInView="whileInView"
          viewport={{ once: true, margin: "-60px" }}
        >
          {plans?.map((plan, index) => (
            <motion.div
              key={plan?.name}
              className={`relative glass-card rounded-3xl sm:rounded-4xl p-6 sm:p-8 lg:p-10 border backdrop-blur-xl transition-all duration-500 hover:-translate-y-2 ${
                plan?.featured
                  ? "border-accent-primary shadow-glow-md scale-100 lg:scale-105"
                  : "border-glass-border hover:border-accent-primary/50"
              }`}
              variants={cardVariant}
            >
              {plan?.badge && (
                <div className="absolute -top-3 sm:-top-4 left-1/2 -translate-x-1/2">
                  <span className="inline-flex items-center gap-1.5 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full bg-accent-primary text-white text-xs sm:text-sm font-bold font-heading shadow-glow-sm">
                    <Icon name="StarIcon" size={14} />
                    {plan?.badge}
                  </span>
                </div>
              )}

              <div className="text-center mb-6 sm:mb-8">
                <h3 className="text-2xl sm:text-3xl font-extrabold font-heading text-text-primary mb-2">
                  {plan?.name}
                </h3>
                <p className="text-xs sm:text-sm text-text-secondary mb-4 sm:mb-6">
                  {plan?.tagline}
                </p>
                <div className="mb-2">
                  <span className="text-4xl sm:text-5xl font-extrabold font-heading text-accent-primary">
                    {plan?.price}
                  </span>
                </div>
                <p className="text-xs sm:text-sm text-text-secondary">
                  {plan?.priceNote}
                </p>
              </div>

              <ul className="space-y-3 sm:space-y-4 mb-6 sm:mb-8">
                {plan?.features?.map((feature, i) => (
                  <li
                    key={i}
                    className={`flex items-start gap-2.5 sm:gap-3 text-xs sm:text-sm ${
                      feature?.included ? "text-text-primary" : "text-text-secondary opacity-50"
                    }`}
                  >
                    <Icon
                      name={feature?.included ? "CheckCircleIcon" : "XCircleIcon"}
                      size={18}
                      className={feature?.included ? "text-accent-primary shrink-0 mt-0.5" : "text-text-secondary shrink-0 mt-0.5"}
                    />
                    <span>{feature?.text}</span>
                  </li>
                ))}
              </ul>

              <Link
                href="/contact"
                className={`flex items-center justify-center gap-2 w-full px-6 py-3.5 sm:py-4 rounded-2xl font-bold text-sm sm:text-base transition-all duration-300 hover:scale-[1.02] min-h-[52px] sm:min-h-[56px] ${
                  plan?.featured
                    ? "bg-accent-primary text-white shadow-glow-sm hover:shadow-glow-md"
                    : "bg-glass-white border border-glass-border text-text-primary hover:border-accent-primary"
                }`}
              >
                {plan?.cta}
                <Icon name="ArrowRightIcon" size={16} />
              </Link>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}